import { getLinearAccessors } from './get-linear-accessors.js';
import { getCurrentScale } from './get-scale.js';
import {
  getAccessors,
  getPixelDimensionsForDatapoint,
} from './query-hovered.js';
import { relAccessor } from './value-representation.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  bandY0Accessor,
  bandY1Accessor,
} from '../components/displays/band-accessors.js';
import type { SeriesQueryResult } from '../context/XYChartOverlayPerformance.context.js';
import type { AxisIdToScales } from '../context/XYChartScales.context.js';
import { AREA_VARIANT, BAND_VARIANT } from '../types/xy-chart-internals.js';
import type {
  AreaSeriesWithDatapoints,
  AxisIdToScaleDomain,
  BarSeriesWithDatapoints,
  LineSeriesWithDatapoints,
  MapDatapoints,
  SeriesId,
  SeriesToMetadata,
} from '../types/xy-chart-internals.js';
import type {
  AreaDatapoint,
  BarDatapoint,
  LineDatapoint,
} from '../types/xy-chart.js';

export type SortedEntry = {
  baselinePixel: number;
  value0Pixel: number;
  value1Pixel: number;
  isHorizontal: boolean;
  /**
   * Stable cluster identifier built from the datapoint's domain-axis
   * coordinates. Entries sharing the same `boundaries` belong to the same
   * stack / same start-end domain group.
   */
  boundaries: string;
  result: SeriesQueryResult;
};

/**
 * Sorted-entry buckets keyed by shape. `bar`, `line`, and `area` are each
 * sorted ascending by `baselinePixel`. `combined` is the concatenation of all
 * three, also sorted ascending by `baselinePixel`, and is used for the
 * cross-shape binary-search cluster lookup that spans all shapes.
 *
 * Selection state in the shared tooltip is tracked per shape, so the
 * underlying data structure separates entries by shape even when only one is
 * in use.
 */
export type SortedEntriesPerShape = {
  bar: SortedEntry[];
  line: SortedEntry[];
  area: SortedEntry[];
  combined: SortedEntry[];
};

export type BarPixelCenter = {
  baselinePixel: number;
  value0Pixel: number;
  value1Pixel: number;
  isHorizontal: boolean;
  boundaries: string;
};

/**
 * Maps a (series, datapoint) pair to its bar's pixel center and
 * stack/cluster boundary. Used to anchor cluster lookups around the
 * hovered bar's exact pixel position instead of the cursor's.
 */
export type GetBarPixelCenter = (
  series: BarSeriesWithDatapoints,
  datapoint: BarDatapoint,
) => BarPixelCenter;

/**
 * Maps a (series, datapoint) pair to its line or area pixel center.
 * Handles both line and area series with a single builder — the accessor
 * selection (`yMiddleBandAccessor` for bands, `y1AreaAccessor` for plain
 * areas, etc.) is delegated to `getLinearAccessors`.
 */
export type GetLinearSeriesPixelCenter = (
  series: LineSeriesWithDatapoints | AreaSeriesWithDatapoints,
  datapoint: LineDatapoint | AreaDatapoint,
) => BarPixelCenter;

/**
 * Build a per-series-cached `GetBarPixelCenter`. Accessor construction
 * is non-trivial, so we memoize per series id to keep tight loops fast.
 *
 * For horizontal bars (y0Accessor + x1Accessor): the domain axis is Y, so
 * `baselinePixel` is the bar's vertical center and `value0/1Pixel` are the
 * left/right X edges. For vertical bars the roles are reversed.
 */
export function buildGetBarPixelCenter(
  axisIdToScales: AxisIdToScales,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  datapointMetadataMap: MapDatapoints,
): GetBarPixelCenter {
  const cachesBySeries = new Map<
    SeriesId,
    { accessors: ReturnType<typeof getAccessors>; isHorizontal: boolean }
  >();

  return (series, datapoint) => {
    let cached = cachesBySeries.get(series.id);
    if (!cached) {
      const metadata = safeGet(seriesToMetadata, series);
      const xRelAccessor = relAccessor(metadata.xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(metadata.yAxis, axisIdToScaleDomain);
      const accessors = getAccessors(
        series,
        axisIdToScales,
        datapointMetadataMap,
        xRelAccessor,
        yRelAccessor,
      );
      cached = {
        accessors,
        isHorizontal: metadata.chartOrientation === 'horizontal',
      };
      cachesBySeries.set(series.id, cached);
    }
    const { x, y, width, height } = getPixelDimensionsForDatapoint(
      datapoint,
      cached.accessors,
    );
    if (cached.isHorizontal) {
      return {
        baselinePixel: y + height / 2,
        value0Pixel: x,
        value1Pixel: x + width,
        isHorizontal: true,
        boundaries: `${String(datapoint.y0)}-${String(datapoint.y1)}`,
      };
    }
    return {
      baselinePixel: x + width / 2,
      value0Pixel: y,
      value1Pixel: y + height,
      isHorizontal: false,
      boundaries: `${String(datapoint.x0)}-${String(datapoint.x1)}`,
    };
  };
}

/**
 * Build a per-series-cached `GetLinearSeriesPixelCenter` for both line and
 * area series. Uses the display accessors from `getLinearAccessors` — x
 * centers time bands and uses x0 for numeric/categorical; y resolves to
 * `yMiddleBandAccessor` for bands (AC4 mid(y0,y1)) or `y1AreaAccessor` for
 * plain areas. Band areas also preserve their scaled `y0`/`y1` edges so
 * shared-hover distance checks can use the full band span. Memoizes per
 * series id to keep tight loops fast.
 */
export function buildGetLinearSeriesPixelCenter(
  axisIdToScales: AxisIdToScales,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  datapointMetadataMap: MapDatapoints,
): GetLinearSeriesPixelCenter {
  const accessorsBySeries = new Map<
    SeriesId,
    {
      xPixelFn: (d: LineDatapoint | AreaDatapoint) => number;
      yPixelFn: (d: LineDatapoint | AreaDatapoint) => number;
      y0PixelFn?: (d: AreaDatapoint) => number;
      y1PixelFn?: (d: AreaDatapoint) => number;
    }
  >();

  return (series, datapoint) => {
    let cached = accessorsBySeries.get(series.id);
    if (!cached) {
      const metadata = safeGet(seriesToMetadata, series);
      const xRelAccessor = relAccessor(metadata.xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(metadata.yAxis, axisIdToScaleDomain);
      const { xAccessor, yAccessor } = getLinearAccessors(
        series.type,
        metadata,
        datapointMetadataMap,
        xRelAccessor,
        yRelAccessor,
      );
      const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
      const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
      const isBandArea =
        series.type === AREA_VARIANT &&
        'variant' in metadata &&
        metadata.variant === BAND_VARIANT;
      cached = {
        xPixelFn: xAccessor(xScale) as (
          d: LineDatapoint | AreaDatapoint,
        ) => number,
        yPixelFn: yAccessor(yScale) as (
          d: LineDatapoint | AreaDatapoint,
        ) => number,
        ...(isBandArea
          ? {
              y0PixelFn: bandY0Accessor(yScale, yRelAccessor) as (
                d: AreaDatapoint,
              ) => number,
              y1PixelFn: bandY1Accessor(yScale, yRelAccessor) as (
                d: AreaDatapoint,
              ) => number,
            }
          : {}),
      };
      accessorsBySeries.set(series.id, cached);
    }
    const yPixel = cached.yPixelFn(datapoint);
    return {
      baselinePixel: cached.xPixelFn(datapoint),
      value0Pixel: cached.y0PixelFn?.(datapoint) ?? yPixel,
      value1Pixel: cached.y1PixelFn?.(datapoint) ?? yPixel,
      isHorizontal: false,
      boundaries: `${String(datapoint.x0)}-${String(datapoint.x1)}`,
    };
  };
}

// Overloads ensure call sites pass matching series/function pairs.
function collectSortedEntries(
  seriesArray: BarSeriesWithDatapoints[],
  datapointMetadataMap: MapDatapoints,
  getPixelCenter: GetBarPixelCenter,
): SortedEntry[];
function collectSortedEntries(
  seriesArray: (LineSeriesWithDatapoints | AreaSeriesWithDatapoints)[],
  datapointMetadataMap: MapDatapoints,
  getPixelCenter: GetLinearSeriesPixelCenter,
): SortedEntry[];
function collectSortedEntries(
  seriesArray: (
    | BarSeriesWithDatapoints
    | LineSeriesWithDatapoints
    | AreaSeriesWithDatapoints
  )[],
  datapointMetadataMap: MapDatapoints,
  getPixelCenter: GetBarPixelCenter | GetLinearSeriesPixelCenter,
): SortedEntry[] {
  // Cast once: the overloads above guarantee series and fn are a matched pair.
  type AnyFn = (
    series:
      | BarSeriesWithDatapoints
      | LineSeriesWithDatapoints
      | AreaSeriesWithDatapoints,
    datapoint: BarDatapoint | LineDatapoint | AreaDatapoint,
  ) => BarPixelCenter;
  const fn = getPixelCenter as unknown as AnyFn;

  const entries: SortedEntry[] = [];
  for (const series of seriesArray) {
    for (const datapoint of series.data as (
      | BarDatapoint
      | LineDatapoint
      | AreaDatapoint
    )[]) {
      if (safeGet(datapointMetadataMap, datapoint).isGap) {
        continue;
      }
      const {
        baselinePixel,
        value0Pixel,
        value1Pixel,
        isHorizontal,
        boundaries,
      } = fn(series, datapoint);
      entries.push({
        baselinePixel,
        value0Pixel,
        value1Pixel,
        isHorizontal,
        boundaries,
        result: {
          type: series.type,
          distance: 0,
          datapoint,
          series,
        } as unknown as SeriesQueryResult,
      });
    }
  }
  return entries;
}

/**
 * Build the per-shape sorted entries used by the shared tooltip query.
 *
 * Bar, line, and area series are each processed into their own bucket.
 * Each datapoint becomes one entry positioned at the series' pixel center,
 * with a `boundaries` key that groups entries sharing the same `x0-x1`.
 * Gap datapoints are skipped.
 *
 * Each per-shape bucket is sorted ascending by `baselinePixel`. `combined`
 * merges all three buckets and is also sorted ascending by `baselinePixel`,
 * ready for a cross-shape binary search cluster lookup.
 */
export function buildSortedEntries(
  barSeries: BarSeriesWithDatapoints[],
  lineSeries: LineSeriesWithDatapoints[],
  areaSeries: AreaSeriesWithDatapoints[],
  datapointMetadataMap: MapDatapoints,
  getBarPixelCenter: GetBarPixelCenter,
  getLinearSeriesPixelCenter: GetLinearSeriesPixelCenter,
): SortedEntriesPerShape {
  const bar = collectSortedEntries(
    barSeries,
    datapointMetadataMap,
    getBarPixelCenter,
  );
  const line = collectSortedEntries(
    lineSeries,
    datapointMetadataMap,
    getLinearSeriesPixelCenter,
  );
  const area = collectSortedEntries(
    areaSeries,
    datapointMetadataMap,
    getLinearSeriesPixelCenter,
  );

  const byDomain = (a: SortedEntry, b: SortedEntry) =>
    a.baselinePixel - b.baselinePixel;
  bar.sort(byDomain);
  line.sort(byDomain);
  area.sort(byDomain);

  const combined = [...bar, ...line, ...area].sort(byDomain);

  return { bar, line, area, combined };
}
